import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShieldCheck,
  TrendingUp,
  TrendingDown,
  Layers,
  Award,
  BookOpen,
  Activity,
  CheckCircle,
  Clock,
  ArrowLeft,
  Calendar,
  DollarSign,
  Percent,
  Briefcase,
  Plus,
  Trash2,
  AlertTriangle,
  ChevronRight,
  RefreshCw,
  Home as HomeIcon,
  GraduationCap,
  ArrowRight
} from 'lucide-react';
import Header from '../header';
import Footer from '../Footer';
import kfintechlogo from "../../Assests/KFINTECHlogo.png";
import camslogo from "../../Assests/camslogo.jpg";
import nsemf2 from "../../Assests/nsenmf.jpg";
import mfutility from "../../Assests/mfutilities.jpg";


// Specialized styling injected directly for background grids, glowing animations, etc.
const INJECTED_MUTUAL_FUNDS_STYLES = `
  .custom-mf-grain {
    background-image: radial-gradient(rgba(14, 165, 233, 0.15) 1px, transparent 0);
    background-size: 24px 24px;
  }
  .glow-border {
    position: relative;
  }
  .glow-border::after {
    content: '';
    position: absolute;
    inset: -1px;
    background: linear-gradient(90deg, #06b6d4, #3b82f6, #6366f1);
    border-radius: inherit;
    z-index: -1;
    opacity: 0.15;
    transition: opacity 0.3s ease;
  }
  .glow-border:hover::after {
    opacity: 0.4;
  }
  /* Custom scrollbar for cashflow table */
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.02);
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(14, 165, 233, 0.2);
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(14, 165, 233, 0.4);
  }
`;

// Format Indian Currency
const formatCurrency = (val) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(val);
};

// Newton-Raphson XIRR Numerical Solver
const calculateXIRR = (cashFlows) => {
  if (cashFlows.length < 2) return null;

  const maxIterations = 100;
  const precision = 1e-6;
  let r = 0.1; // Initial guess: 10%

  const sortedCF = [...cashFlows].sort((a, b) => new Date(a.date) - new Date(b.date));
  const d0 = new Date(sortedCF[0].date);

  const getF = (rate) => {
    return sortedCF.reduce((sum, cf) => {
      const days = (new Date(cf.date) - d0) / (24 * 60 * 60 * 1000);
      return sum + cf.amount / Math.pow(1 + rate, days / 365);
    }, 0);
  };

  const getDF = (rate) => {
    return sortedCF.reduce((sum, cf) => {
      const days = (new Date(cf.date) - d0) / (24 * 60 * 60 * 1000);
      return sum - (days / 365) * cf.amount / Math.pow(1 + rate, (days / 365) + 1);
    }, 0);
  };

  for (let i = 0; i < maxIterations; i++) {
    const f = getF(r);
    const df = getDF(r);
    if (Math.abs(df) < 1e-12) break;
    const nextR = r - f / df;
    if (Math.abs(nextR - r) < precision) {
      return nextR * 100; // Return as percentage
    }
    r = nextR;
  }
  return null;
};

const MutualFunds = () => {
  // Navigation back handler
  const handleBack = () => {
    window.location.href = '/';
  };

  // State for calculators
  const [activeTab, setActiveTab] = useState('sip');

  // SIP Calculator State
  const [sipMonthly, setSipMonthly] = useState(10000);
  const [sipRate, setSipRate] = useState(12);
  const [sipYears, setSipYears] = useState(15);

  // Lumpsum State
  const [lumpsumPrincipal, setLumpsumPrincipal] = useState(100000);
  const [lumpsumRate, setLumpsumRate] = useState(12);
  const [lumpsumYears, setLumpsumYears] = useState(15);

  // SWP State
  const [swpPrincipal, setSwpPrincipal] = useState(1000000);
  const [swpWithdrawal, setSwpWithdrawal] = useState(6000);
  const [swpRate, setSwpRate] = useState(8);
  const [swpYears, setSwpYears] = useState(15);

  // Child Education State
  const [childCurrentAge, setChildCurrentAge] = useState(3);
  const [childCollegeAge, setChildCollegeAge] = useState(18);
  const [educationCurrentCost, setEducationCurrentCost] = useState(1000000);
  const [educationInflation, setEducationInflation] = useState(7);
  const [educationRate, setEducationRate] = useState(12);

  // Retirement State
  const [retirementCurrentAge, setRetirementCurrentAge] = useState(30);
  const [retirementTargetAge, setRetirementTargetAge] = useState(60);
  const [retirementExpenses, setRetirementExpenses] = useState(50000);
  const [retirementInflation, setRetirementInflation] = useState(6);
  const [retirementReturnPre, setRetirementReturnPre] = useState(12);
  const [retirementReturnPost, setRetirementReturnPost] = useState(8);

  // Home State
  const [homeCurrentCost, setHomeCurrentCost] = useState(5000000);
  const [homeYears, setHomeYears] = useState(10);
  const [homeInflation, setHomeInflation] = useState(6);
  const [homeRate, setHomeRate] = useState(12);

  // Step up SIP State
  const [stepupMonthly, setStepupMonthly] = useState(10000);
  const [stepupAnnualGrowth, setStepupAnnualGrowth] = useState(10);
  const [stepupRate, setStepupRate] = useState(12);
  const [stepupYears, setStepupYears] = useState(15);

  // XIRR State
  const [xirrFlows, setXirrFlows] = useState([
    { id: 1, date: '2022-01-01', amount: -100000 },
    { id: 2, date: '2023-01-01', amount: -50000 },
    { id: 3, date: '2024-01-01', amount: -50000 },
    { id: 4, date: '2025-05-31', amount: 265000 }
  ]);
  const [xirrDateInput, setXirrDateInput] = useState('');
  const [xirrAmountInput, setXirrAmountInput] = useState('');
  const [xirrResult, setXirrResult] = useState(null);

  // Roadmap to 1-Crore State
  const [roadMapSIP, setRoadMapSIP] = useState(15000);
  const [roadMapRate, setRoadMapRate] = useState(12);

  // Live calculation of 1-Crore Roadmap milestones
  const roadmapMilestones = useMemo(() => {
    const i = roadMapRate / 12 / 100;
    const targets = [1000000, 2500000, 5000000, 10000000];
    const titles = ['10 Lakhs (Launchpad)', '25 Lakhs (Momentum)', '50 Lakhs (Acceleration)', '1 Crore (Snowball)'];
    const descriptions = [
      'The initial phase where compounding starts taking off slowly.',
      'The milestone where compounding returns begin to show clear momentum.',
      'The threshold where returns represent a massive portion of asset growth.',
      'The apex wealth goal where investment returns eclipse monthly contributions.'
    ];

    return targets.map((target, idx) => {
      if (!roadMapSIP || roadMapSIP <= 0) {
        return {
          title: titles[idx],
          description: descriptions[idx],
          years: '∞',
          invested: 0,
          returns: 0,
          target
        };
      }
      // Find months needed: Target = SIP * [((1+i)^n - 1)/i] * (1+i)
      // Solving for n: (Target * i) / (SIP * (1+i)) + 1 = (1+i)^n
      // n = log((Target * i) / (SIP * (1+i)) + 1) / log(1+i)
      const numerator = (target * i) / (roadMapSIP * (1 + i)) + 1;
      let totalMonths = 0;
      if (numerator > 0) {
        totalMonths = Math.log(numerator) / Math.log(1 + i);
      }

      const totalYears = totalMonths / 12;
      const totalInvested = roadMapSIP * Math.round(totalMonths);
      const returns = target - totalInvested;

      return {
        title: titles[idx],
        description: descriptions[idx],
        years: totalYears > 0 ? totalYears.toFixed(1) : '∞',
        invested: Math.round(totalInvested),
        returns: Math.round(returns),
        target
      };
    });
  }, [roadMapSIP, roadMapRate]);

  // Handle adding XIRR Flow
  const handleAddXirrFlow = () => {
    if (!xirrDateInput || !xirrAmountInput) return;
    const flow = {
      id: Date.now(),
      date: xirrDateInput,
      amount: parseFloat(xirrAmountInput)
    };
    setXirrFlows([...xirrFlows, flow]);
    setXirrAmountInput('');
  };

  // Handle removing XIRR Flow
  const handleRemoveXirrFlow = (id) => {
    setXirrFlows(xirrFlows.filter(flow => flow.id !== id));
  };

  // Run XIRR Solver
  const handleCalculateXIRR = () => {
    const rate = calculateXIRR(xirrFlows);
    setXirrResult(rate);
  };

  // Pre-solve XIRR on load or flow change
  useEffect(() => {
    handleCalculateXIRR();
  }, [xirrFlows]);

  // SIP Math Calculations
  const sipCalculation = useMemo(() => {
    const i = sipRate / 12 / 100;
    const months = sipYears * 12;
    const invested = sipMonthly * months;
    const total = sipMonthly * ((Math.pow(1 + i, months) - 1) / i) * (1 + i);
    const gains = total - invested;
    return {
      invested: Math.round(invested),
      gains: Math.round(gains),
      total: Math.round(total)
    };
  }, [sipMonthly, sipRate, sipYears]);

  // Lumpsum Math Calculations
  const lumpsumCalculation = useMemo(() => {
    const total = lumpsumPrincipal * Math.pow(1 + lumpsumRate / 100, lumpsumYears);
    const invested = lumpsumPrincipal;
    const gains = total - invested;
    return {
      invested: Math.round(invested),
      gains: Math.round(gains),
      total: Math.round(total)
    };
  }, [lumpsumPrincipal, lumpsumRate, lumpsumYears]);

  // SWP Math Calculations
  const swpCalculation = useMemo(() => {
    // Use effective monthly rate from annual CAGR: (1 + R)^(1/12) - 1
    const monthlyRate = Math.pow(1 + swpRate / 100, 1 / 12) - 1;
    const months = swpYears * 12;
    let balance = swpPrincipal;
    let totalWithdrawn = 0;

    for (let m = 0; m < months; m++) {
      balance = balance * (1 + monthlyRate) - swpWithdrawal;
      totalWithdrawn += swpWithdrawal;
      if (balance <= 0) {
        balance = 0;
        break;
      }
    }

    return {
      finalBalance: Math.round(balance),
      totalWithdrawn: Math.round(totalWithdrawn),
      warning: swpWithdrawal * 12 > swpPrincipal * 0.12 // Warning if withdrawal is > 12% yield annually
    };
  }, [swpPrincipal, swpWithdrawal, swpRate, swpYears]);

  // Child Education Math
  const childEducationCalculation = useMemo(() => {
    const yearsToCollege = Math.max(1, childCollegeAge - childCurrentAge);
    const futureCost = educationCurrentCost * Math.pow(1 + educationInflation / 100, yearsToCollege);

    // FutureCost = SIP * [((1+i)^n - 1)/i] * (1+i)
    const i = educationRate / 12 / 100;
    const months = yearsToCollege * 12;
    const requiredSIP = futureCost / (((Math.pow(1 + i, months) - 1) / i) * (1 + i));
    const totalInvested = requiredSIP * months;

    return {
      yearsToCollege,
      futureCost: Math.round(futureCost),
      requiredSIP: Math.round(requiredSIP),
      totalInvested: Math.round(totalInvested),
      gains: Math.round(futureCost - totalInvested)
    };
  }, [childCurrentAge, childCollegeAge, educationCurrentCost, educationInflation, educationRate]);

  // Retirement Math
  const retirementCalculation = useMemo(() => {
    const yearsToRetire = Math.max(1, retirementTargetAge - retirementCurrentAge);
    const monthlyExpenseAtRetirement = retirementExpenses * Math.pow(1 + retirementInflation / 100, yearsToRetire);

    // Pension needed for 25 years (from retirement till life expectancy of 85 years)
    const postYears = 25;
    const totalMonthsPost = postYears * 12;
    const postMonthlyRate = retirementReturnPost / 12 / 100;
    const inflationMonthlyRate = retirementInflation / 12 / 100;

    // Inflation adjusted pension rate
    const rAdj = (1 + postMonthlyRate) / (1 + inflationMonthlyRate) - 1;

    // Present value of annuity due (adjusting for ongoing inflation post-retirement)
    const corpusNeeded = monthlyExpenseAtRetirement * ((1 - Math.pow(1 + rAdj, -totalMonthsPost)) / rAdj) * (1 + rAdj);

    // Required SIP to build this corpus
    const preMonthlyRate = retirementReturnPre / 12 / 100;
    const totalMonthsPre = yearsToRetire * 12;
    const requiredSIP = corpusNeeded / (((Math.pow(1 + preMonthlyRate, totalMonthsPre) - 1) / preMonthlyRate) * (1 + preMonthlyRate));

    return {
      yearsToRetire,
      futureExpense: Math.round(monthlyExpenseAtRetirement),
      corpusNeeded: Math.round(corpusNeeded),
      requiredSIP: Math.round(requiredSIP)
    };
  }, [retirementCurrentAge, retirementTargetAge, retirementExpenses, retirementInflation, retirementReturnPre, retirementReturnPost]);

  // Home Math
  const homeCalculation = useMemo(() => {
    const futureCost = homeCurrentCost * Math.pow(1 + homeInflation / 100, homeYears);
    const i = homeRate / 12 / 100;
    const months = homeYears * 12;
    const requiredSIP = futureCost / (((Math.pow(1 + i, months) - 1) / i) * (1 + i));
    const totalInvested = requiredSIP * months;

    return {
      futureCost: Math.round(futureCost),
      requiredSIP: Math.round(requiredSIP),
      totalInvested: Math.round(totalInvested),
      gains: Math.round(futureCost - totalInvested)
    };
  }, [homeCurrentCost, homeYears, homeInflation, homeRate]);

  // Step up SIP Math
  const stepupCalculation = useMemo(() => {
    const r = Math.pow(1 + stepupRate / 100, 1 / 12) - 1;
    const stepupFraction = stepupAnnualGrowth / 100;

    let totalInvested = 0;
    let balance = 0;
    let monthlySip = stepupMonthly;

    for (let year = 1; year <= stepupYears; year++) {
      for (let month = 1; month <= 12; month++) {
        totalInvested += monthlySip;
        balance = (balance + monthlySip) * (1 + r);
      }
      monthlySip = monthlySip * (1 + stepupFraction);
    }

    return {
      invested: Math.round(totalInvested),
      total: Math.round(balance),
      gains: Math.round(balance - totalInvested)
    };
  }, [stepupMonthly, stepupAnnualGrowth, stepupRate, stepupYears]);

  return (
    <div className="min-h-screen bg-[#010411] text-slate-100 font-sans selection:bg-cyan-500 selection:text-black overflow-x-hidden relative custom-mf-grain">
      <style>{INJECTED_MUTUAL_FUNDS_STYLES}</style>

      {/* Futuristic Cinematic Glowing Nodes Background */}
      <div className="absolute top-0 inset-x-0 h-[64rem] bg-gradient-to-b from-[#051139] via-[#01051c] to-[#010411] pointer-events-none z-0" />
      <div className="fixed top-0 left-1/4 w-[700px] h-[700px] bg-blue-600/5 rounded-full filter blur-[140px] pointer-events-none z-0" />
      <div className="fixed top-1/3 right-1/4 w-[800px] h-[800px] bg-cyan-600/5 rounded-full filter blur-[160px] pointer-events-none z-0" />

      {/* Global Navigation Header */}
      <Header />

      {/* Cinematic Hero Area */}
      <section className="relative pt-12 pb-20 px-4 sm:px-6 lg:px-8 z-10 text-center">
        <div className="max-w-4xl mx-auto space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-[10px] font-black tracking-widest text-cyan-400 uppercase"
          >
            <Award size={14} className="animate-pulse" />
            <span>Premium Wealth Compounding Architecture</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-6xl font-black text-white leading-tight"
          >
            {/* Sovereign Goal-Based <br /> */}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-200 to-indigo-500">Believe in Goal-Based Investment</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-slate-300 text-lg sm:text-xl max-w-2xl mx-auto leading-relaxed"
          >
            Break away from the vulnerability of single-company exposure. Diversify instantly with customized active & passive asset classes engineered for multi-generational security.
          </motion.p>
        </div>
      </section>

      {/* Philosophy Section */}
      <section id="philosophy" className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

          {/* Card 1: Believe in Goal-based Investment */}
          <motion.div
            whileHover={{ y: -4 }}
            transition={{ duration: 0.3 }}
            className="bg-[#050a24]/60 backdrop-blur-xl border border-white/10 rounded-[2rem] p-8 lg:p-10 shadow-2xl relative overflow-hidden flex flex-col justify-between"
          >
            <div className="absolute top-0 right-0 w-48 h-48 bg-blue-500/5 rounded-full filter blur-2xl pointer-events-none" />
            <div className="space-y-6">
              <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
                <Layers size={24} />
              </div>
              <h2 className="text-2xl lg:text-3xl font-black text-white">Strategic Asset Allocation</h2>
              <p className="text-slate-300 leading-relaxed text-sm lg:text-base">
                Instead of concentrated risk in a single asset or spreading capital blindly, structured Mutual Funds provide immediate risk-mitigated wealth creation.
              </p>

              <div className="grid grid-cols-2 gap-4 pt-4">
                <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                  <h4 className="font-extrabold text-white text-xs uppercase tracking-wider text-cyan-400">Instant Diversification</h4>
                  <p className="text-slate-400 text-xs mt-1">Spread risk cleanly across hundreds of market leaders.</p>
                </div>
                <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                  <h4 className="font-extrabold text-white text-xs uppercase tracking-wider text-cyan-400">Hands-Off Management</h4>
                  <p className="text-slate-400 text-xs mt-1">Sovereign advisory and active institutional management.</p>
                </div>
                <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                  <h4 className="font-extrabold text-white text-xs uppercase tracking-wider text-cyan-400">Affordable Access</h4>
                  <p className="text-slate-400 text-xs mt-1">Compound large institutional portfolios with small amounts.</p>
                </div>
                <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                  <h4 className="font-extrabold text-white text-xs uppercase tracking-wider text-cyan-400">High Liquidity</h4>
                  <p className="text-slate-400 text-xs mt-1">Deploy and withdraw with immediate convenience.</p>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-white/5 flex items-center justify-between">
              <div>
                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">Managed Frameworks</span>
                <span className="text-sm font-extrabold text-white">Active & Passive Custody</span>
              </div>
              <div className="text-right">
                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">Available Assets</span>
                <span className="text-sm font-extrabold text-cyan-400">Equity, Debt, Hybrid & Bonds</span>
              </div>
            </div>
          </motion.div>

          {/* Card 2: Discipline Matters */}
          <motion.div
            whileHover={{ y: -4 }}
            transition={{ duration: 0.3 }}
            className="bg-gradient-to-br from-[#060c2c] to-[#010411] border border-cyan-500/20 rounded-[2rem] p-8 lg:p-10 shadow-2xl relative overflow-hidden flex flex-col justify-between"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_300px_at_bottom_right,rgba(14,165,233,0.1),transparent)] pointer-events-none" />
            <div className="space-y-6">
              <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center text-indigo-400">
                <Activity size={24} />
              </div>
              <h2 className="text-2xl lg:text-3xl font-black text-white">Discipline Matters</h2>

              <blockquote className="border-l-2 border-cyan-400 pl-4 space-y-2">
                <p className="text-slate-200 font-bold text-lg italic">
                  "Successful investing is not about timing the market. It's about time in the market."
                </p>
              </blockquote>

              <p className="text-slate-300 leading-relaxed text-sm">
                Markets may rise and fall, headlines may change, and emotions may influence decisions. Yet, history has consistently shown that disciplined investors are better positioned to achieve their long-term financial goals.
              </p>

              <div className="space-y-3 pt-2">
                <div className="flex items-center space-x-3 text-xs text-slate-200">
                  <CheckCircle size={16} className="text-cyan-400" />
                  <span>Consistency outperforms sporadic entries.</span>
                </div>
                <div className="flex items-center space-x-3 text-xs text-slate-200">
                  <CheckCircle size={16} className="text-cyan-400" />
                  <span>Patience shields capital from knee-jerk exits.</span>
                </div>
                <div className="flex items-center space-x-3 text-xs text-slate-200">
                  <CheckCircle size={16} className="text-cyan-400" />
                  <span>A well-defined strategy overcomes transient volatility.</span>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-white/5">
              <p className="text-cyan-400 font-bold text-xs uppercase tracking-widest">
                Because wealth is rarely built through a single decision.
              </p>
            </div>
          </motion.div>

        </div>
      </section>

      {/* Road Map to 1-Crore (Premium Interactive Section) */}
      <section id="roadmap" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#010411] via-[#020721] to-[#010411] border-y border-white/5 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto space-y-4 mb-16">
            <span className="text-xs font-black uppercase tracking-[0.25em] text-cyan-400">FINANCIAL TRAJECTORY</span>
            <h2 className="text-3xl sm:text-5xl font-black text-white">Road Map to 1-Crore</h2>
            <p className="text-slate-400 text-sm sm:text-base">
              Customize your monthly savings and compound rate below to visualize your personalized milestone timeline to reach a ₹1 Crore corpus.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">

            {/* Interactive Control Panel */}
            <div className="lg:col-span-4 bg-[#050a24]/50 backdrop-blur-xl border border-white/10 rounded-[2rem] p-6 space-y-8">
              <h3 className="font-extrabold text-lg text-white border-b border-white/5 pb-3">Projection Parameters</h3>

              {/* Slider 1: Monthly Investment */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Monthly SIP</label>
                  <div className="flex items-center space-x-1.5 bg-slate-950/40 border border-white/10 focus-within:border-cyan-400 rounded-lg px-2.5 py-1 transition-all">
                    <span className="text-xs text-slate-500 font-bold">₹</span>
                    <input
                      type="number"
                      min="1000"
                      max="10000000"
                      value={roadMapSIP}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        if (!isNaN(val)) {
                          setRoadMapSIP(val);
                        } else {
                          setRoadMapSIP(0);
                        }
                      }}
                      onBlur={(e) => {
                        const val = parseInt(e.target.value);
                        if (isNaN(val) || val < 1000) {
                          setRoadMapSIP(1000);
                        } else if (val > 10000000) {
                          setRoadMapSIP(10000000);
                        }
                      }}
                      className="w-24 bg-transparent text-right text-xs sm:text-sm font-extrabold text-cyan-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                </div>
                <input
                  type="range"
                  min="1000"
                  max="10000000"
                  step="25000"
                  value={roadMapSIP}
                  onChange={(e) => setRoadMapSIP(parseInt(e.target.value))}
                  className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-cyan-400"
                />
                <div className="flex justify-between text-[10px] text-slate-500 font-bold font-mono">
                  <span>₹1K</span>
                  <span>₹50L</span>
                  <span>₹1Cr</span>
                </div>
              </div>

              {/* Slider 2: Annual Returns */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Expected CAGR</label>
                  <span className="text-base font-extrabold text-indigo-400">{roadMapRate}%</span>
                </div>
                <input
                  type="range"
                  min="6"
                  max="24"
                  step="1"
                  value={roadMapRate}
                  onChange={(e) => setRoadMapRate(parseInt(e.target.value))}
                  className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-indigo-400"
                />
                <div className="flex justify-between text-[10px] text-slate-500 font-bold font-mono">
                  <span>6%</span>
                  <span>15%</span>
                  <span>24%</span>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-cyan-500/5 border border-cyan-500/20 text-xs leading-relaxed text-slate-300">
                <strong className="text-cyan-400 block mb-1">Compound Magic</strong>
                At {roadMapRate}% returns, every rupee invested is heavily amplified. Compounding yields grow exponentially over time.
              </div>
            </div>

            {/* Trajectory Milestone Timeline */}
            <div className="lg:col-span-8 space-y-8 relative">
              {/* Vertical connecting line */}
              <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-gradient-to-b from-cyan-500 via-indigo-500 to-emerald-500 hidden md:block" />

              <div className="space-y-6">
                {roadmapMilestones.map((milestone, idx) => {
                  const prevYears = idx > 0 ? parseFloat(roadmapMilestones[idx - 1].years) : 0;
                  const currentYears = parseFloat(milestone.years);
                  const isFinite = !isNaN(currentYears);
                  const incremental = isFinite && idx > 0 ? (currentYears - prevYears).toFixed(1) : null;

                  return (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: idx * 0.1 }}
                      className="relative md:pl-16 flex flex-col md:flex-row md:items-center justify-between p-6 rounded-3xl bg-[#03071e]/70 border border-white/5 hover:border-cyan-500/20 transition-all gap-6"
                    >
                      {/* Timeline dot */}
                      <div className={`absolute left-[25px] top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full z-10 hidden md:flex items-center justify-center transition-all ${idx === 0 ? 'w-4 h-4 bg-[#03071e] border-2 border-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.5)]' :
                        idx === 1 ? 'w-6 h-6 bg-[#03071e] border-2 border-cyan-400 shadow-[0_0_12px_rgba(34,211,238,0.6)]' :
                          idx === 2 ? 'w-8 h-8 bg-[#03071e] border-2 border-indigo-400 shadow-[0_0_16px_rgba(99,102,241,0.6)]' :
                            'w-12 h-12 bg-gradient-to-br from-[#064e3b] to-[#022c22] border-2 border-emerald-400 shadow-[0_0_24px_rgba(52,211,153,0.8)]'
                        }`}>
                        {idx === 3 && <span className="text-xl drop-shadow-lg" title="1 Crore Milestone Achieved!">🏆</span>}
                      </div>

                      {/* Left side: Title & Description */}
                      <div className="space-y-2 flex-1">
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-slate-400 font-mono">
                            STAGE 0{idx + 1}
                          </span>
                          <h4 className="font-black text-white text-base tracking-wide uppercase flex items-center gap-2">
                            {milestone.title}
                            {idx === 3 && <span className="text-2xl animate-bounce">🚀</span>}
                          </h4>
                        </div>
                        <p className="text-slate-400 text-xs leading-relaxed max-w-xl">{milestone.description}</p>
                      </div>

                      {/* Right side: Years & Money details */}
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 min-w-[280px]">
                        {/* Time box */}
                        <div className="flex-1 bg-cyan-950/20 border border-cyan-500/10 rounded-2xl p-4 text-center relative overflow-hidden">
                          <span className="text-[9px] text-slate-500 font-bold block uppercase tracking-wider font-mono">Time Needed</span>
                          <span className="text-xl font-black text-cyan-400 font-mono block mt-1">
                            {milestone.years} {milestone.years !== '∞' ? 'Years' : ''}
                          </span>
                          {incremental && (
                            <span className="text-[9px] text-emerald-400 font-semibold mt-1 inline-block bg-emerald-500/10 px-2 py-0.5 rounded-full font-mono">
                              +{incremental} yrs from Stage 0{idx}
                            </span>
                          )}
                        </div>

                        {/* Money box */}
                        <div className="flex-1 bg-slate-950/40 border border-white/5 rounded-2xl p-4 font-mono text-xs space-y-1.5 justify-center flex flex-col">
                          <div className="flex justify-between">
                            <span className="text-slate-500">Invested:</span>
                            <span className="text-slate-200 font-bold">{formatCurrency(milestone.invested)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Profit:</span>
                            <span className="text-emerald-400 font-bold">+{formatCurrency(milestone.returns)}</span>
                          </div>
                        </div>
                      </div>

                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Universal Multi-Calculator Suite */}
      <section id="calculators" className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative z-10">
        <div className="text-center max-w-2xl mx-auto space-y-4 mb-16">
          <span className="text-xs font-black uppercase tracking-[0.25em] text-cyan-400">DECISION TERMINAL</span>
          <h2 className="text-3xl sm:text-5xl font-black text-white">Sovereign Financial Calculators</h2>
          <p className="text-slate-400 text-sm sm:text-base">
            Equip your decision-making with high-precision analytical projection calculators mapped to your exact wealth goals.
          </p>
        </div>

        {/* Tab Selection */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {[
            { id: 'sip', name: 'SIP' },
            { id: 'lumpsum', name: 'Lumpsum' },
            { id: 'swp', name: 'SWP' },
            { id: 'child', name: 'Child Education' },
            { id: 'retirement', name: 'Retirement' },
            { id: 'home', name: 'Home Purchase' },
            { id: 'stepup', name: 'Step up SIP' },
            { id: 'xirr', name: 'XIRR Solver' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 rounded-xl text-xs uppercase tracking-wider font-extrabold border transition-all active:scale-95 ${activeTab === tab.id
                ? 'bg-gradient-to-r from-cyan-500 to-blue-600 border-cyan-400 text-white shadow-lg'
                : 'bg-[#040822] border-white/5 hover:border-white/20 text-slate-300'
                }`}
            >
              {tab.name}
            </button>
          ))}
        </div>

        <div className="bg-[#050a24]/40 backdrop-blur-xl border border-white/10 rounded-[2rem] p-6 lg:p-10 shadow-2xl">
          <AnimatePresence mode="wait">

            {/* 1. SIP Calculator */}
            {activeTab === 'sip' && (
              <motion.div
                key="sip"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center"
              >
                <div className="lg:col-span-5 space-y-6">
                  <h3 className="text-xl font-bold text-white uppercase tracking-wider">SIP Compounding Modeler</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-bold text-slate-300">
                        <span>Monthly Investment</span>
                        <span>{formatCurrency(sipMonthly)}</span>
                      </div>
                      <input
                        type="range" min="1000" max="100000" step="1000" value={sipMonthly}
                        onChange={(e) => setSipMonthly(parseInt(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-bold text-slate-300">
                        <span>Expected Return Rate (CAGR)</span>
                        <span>{sipRate}%</span>
                      </div>
                      <input
                        type="range" min="5" max="30" step="0.5" value={sipRate}
                        onChange={(e) => setSipRate(parseFloat(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-bold text-slate-300">
                        <span>Time Period (Years)</span>
                        <span>{sipYears} Yrs</span>
                      </div>
                      <input
                        type="range" min="1" max="40" step="1" value={sipYears}
                        onChange={(e) => setSipYears(parseInt(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                  </div>
                </div>

                {/* Outputs & Custom Pie Chart Graphics */}
                <div className="lg:col-span-7 flex flex-col md:flex-row items-center justify-around gap-6 p-6 rounded-2xl bg-white/5">
                  <div className="space-y-4 font-mono w-full md:w-auto">
                    <div>
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest block">Total Invested</span>
                      <span className="text-lg font-bold text-white">{formatCurrency(sipCalculation.invested)}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest block">Estimated Returns</span>
                      <span className="text-lg font-bold text-emerald-400">+{formatCurrency(sipCalculation.gains)}</span>
                    </div>
                    <div className="pt-3 border-t border-white/5">
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest block">Total wealth</span>
                      <span className="text-2xl font-black text-cyan-400">{formatCurrency(sipCalculation.total)}</span>
                    </div>
                  </div>

                  {/* Stunning Custom SVG Ring chart */}
                  <div className="relative w-40 h-40">
                    <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                      <circle cx="50" cy="50" r="40" stroke="rgba(255,255,255,0.05)" strokeWidth="12" fill="none" />
                      <circle
                        cx="50" cy="50" r="40" stroke="#0ea5e9" strokeWidth="12" fill="none"
                        strokeDasharray="251.2"
                        strokeDashoffset={251.2 - (251.2 * sipCalculation.invested) / sipCalculation.total}
                      />
                      <circle
                        cx="50" cy="50" r="40" stroke="#10b981" strokeWidth="12" fill="none"
                        strokeDasharray="251.2"
                        strokeDashoffset={251.2 - (251.2 * sipCalculation.gains) / sipCalculation.total}
                        className="origin-center transform rotate-180"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                      <span className="text-[9px] text-slate-500 uppercase font-black">Invested</span>
                      <span className="text-xs font-bold text-slate-200">
                        {((sipCalculation.invested / sipCalculation.total) * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* 2. Lumpsum Calculator */}
            {activeTab === 'lumpsum' && (
              <motion.div
                key="lumpsum"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center"
              >
                <div className="lg:col-span-5 space-y-6">
                  <h3 className="text-xl font-bold text-white uppercase tracking-wider">Lumpsum Modeler</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-bold text-slate-300">
                        <span>Total Investment</span>
                        <span>{formatCurrency(lumpsumPrincipal)}</span>
                      </div>
                      <input
                        type="range" min="10000" max="20000000" step="10000" value={lumpsumPrincipal}
                        onChange={(e) => setLumpsumPrincipal(parseInt(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-bold text-slate-300">
                        <span>Expected Return Rate (CAGR)</span>
                        <span>{lumpsumRate}%</span>
                      </div>
                      <input
                        type="range" min="5" max="30" step="0.5" value={lumpsumRate}
                        onChange={(e) => setLumpsumRate(parseFloat(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-bold text-slate-300">
                        <span>Time Period (Years)</span>
                        <span>{lumpsumYears} Yrs</span>
                      </div>
                      <input
                        type="range" min="1" max="40" step="1" value={lumpsumYears}
                        onChange={(e) => setLumpsumYears(parseInt(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-7 flex flex-col md:flex-row items-center justify-around gap-6 p-6 rounded-2xl bg-white/5">
                  <div className="space-y-4 font-mono w-full md:w-auto">
                    <div>
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest block">Total Invested</span>
                      <span className="text-lg font-bold text-white">{formatCurrency(lumpsumCalculation.invested)}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest block">Estimated Returns</span>
                      <span className="text-lg font-bold text-emerald-400">+{formatCurrency(lumpsumCalculation.gains)}</span>
                    </div>
                    <div className="pt-3 border-t border-white/5">
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest block">Total wealth</span>
                      <span className="text-2xl font-black text-cyan-400">{formatCurrency(lumpsumCalculation.total)}</span>
                    </div>
                  </div>

                  <div className="relative w-40 h-40">
                    <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                      <circle cx="50" cy="50" r="40" stroke="rgba(255,255,255,0.05)" strokeWidth="12" fill="none" />
                      <circle
                        cx="50" cy="50" r="40" stroke="#0ea5e9" strokeWidth="12" fill="none"
                        strokeDasharray="251.2"
                        strokeDashoffset={251.2 - (251.2 * lumpsumCalculation.invested) / lumpsumCalculation.total}
                      />
                      <circle
                        cx="50" cy="50" r="40" stroke="#10b981" strokeWidth="12" fill="none"
                        strokeDasharray="251.2"
                        strokeDashoffset={251.2 - (251.2 * lumpsumCalculation.gains) / lumpsumCalculation.total}
                        className="origin-center transform rotate-180"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                      <span className="text-[9px] text-slate-500 uppercase font-black">Yield Multiple</span>
                      <span className="text-xs font-bold text-slate-200">
                        {(lumpsumCalculation.total / lumpsumCalculation.invested).toFixed(1)}x
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* 3. SWP Calculator */}
            {activeTab === 'swp' && (
              <motion.div
                key="swp"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center"
              >
                <div className="lg:col-span-5 space-y-6">
                  <h3 className="text-xl font-bold text-white uppercase tracking-wider">SWP Cashflow planner</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-bold text-slate-300">
                        <span>Total Investment</span>
                        <span>{formatCurrency(swpPrincipal)}</span>
                      </div>
                      <input
                        type="range" min="100000" max="20000000" step="100000" value={swpPrincipal}
                        onChange={(e) => setSwpPrincipal(parseInt(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-bold text-slate-300">
                        <span>Monthly Withdrawal</span>
                        <span>{formatCurrency(swpWithdrawal)}</span>
                      </div>
                      <input
                        type="range" min="1000" max="200000" step="1000" value={swpWithdrawal}
                        onChange={(e) => setSwpWithdrawal(parseInt(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-bold text-slate-300">
                        <span>Expected Return (CAGR)</span>
                        <span>{swpRate}%</span>
                      </div>
                      <input
                        type="range" min="5" max="20" step="0.5" value={swpRate}
                        onChange={(e) => setSwpRate(parseFloat(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-bold text-slate-300">
                        <span>Time Period (Years)</span>
                        <span>{swpYears} Yrs</span>
                      </div>
                      <input
                        type="range" min="1" max="30" step="1" value={swpYears}
                        onChange={(e) => setSwpYears(parseInt(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-7 space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-6 rounded-2xl bg-white/5 font-mono text-center">
                    <div>
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest block">Total Invested</span>
                      <span className="text-sm font-bold text-white">{formatCurrency(swpPrincipal)}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest block">Total Payouts</span>
                      <span className="text-sm font-bold text-emerald-400">{formatCurrency(swpCalculation.totalWithdrawn)}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest block">Remaining Balance</span>
                      <span className="text-sm font-bold text-cyan-400">{formatCurrency(swpCalculation.finalBalance)}</span>
                    </div>
                  </div>

                  {swpCalculation.warning ? (
                    <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-start gap-3">
                      <AlertTriangle size={20} className="text-amber-400 shrink-0 mt-0.5" />
                      <div className="text-xs text-slate-300">
                        <strong className="text-amber-400 block mb-1">Depletion Risk Alert</strong>
                        Your withdrawal rate is highly aggressive ({((swpWithdrawal * 12 / swpPrincipal) * 100).toFixed(1)}% annually). This could deplete your core wealth corpus rapidly in actual markets.
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-start gap-3">
                      <CheckCircle size={20} className="text-emerald-400 shrink-0 mt-0.5" />
                      <div className="text-xs text-slate-300">
                        <strong className="text-emerald-400 block mb-1">Sustainable Growth Path</strong>
                        Your withdrawal model falls within standard safe advisory yield metrics ({((swpWithdrawal * 12 / swpPrincipal) * 100).toFixed(1)}% annually). Capital preservation looks robust.
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* 4. Child Education */}
            {activeTab === 'child' && (
              <motion.div
                key="child"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center"
              >
                <div className="lg:col-span-5 space-y-6">
                  <h3 className="text-xl font-bold text-white uppercase tracking-wider flex items-center gap-2">
                    <GraduationCap className="text-cyan-400" /> Child Education Planner
                  </h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-300">Child's Current Age</label>
                        <input
                          type="number" min="0" max="17" value={childCurrentAge}
                          onChange={(e) => setChildCurrentAge(parseInt(e.target.value) || 0)}
                          className="w-full bg-slate-900 border border-white/10 rounded-xl px-3 py-2 text-white font-mono text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-300">Target College Age</label>
                        <input
                          type="number" min="18" max="25" value={childCollegeAge}
                          onChange={(e) => setChildCollegeAge(parseInt(e.target.value) || 18)}
                          className="w-full bg-slate-900 border border-white/10 rounded-xl px-3 py-2 text-white font-mono text-sm"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-bold text-slate-300">
                        <span>Current Admission/College Cost</span>
                        <span>{formatCurrency(educationCurrentCost)}</span>
                      </div>
                      <input
                        type="range" min="100000" max="10000000" step="100000" value={educationCurrentCost}
                        onChange={(e) => setEducationCurrentCost(parseInt(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-300">Expected Inflation (%)</label>
                        <input
                          type="number" min="4" max="15" value={educationInflation}
                          onChange={(e) => setEducationInflation(parseFloat(e.target.value) || 0)}
                          className="w-full bg-slate-900 border border-white/10 rounded-xl px-3 py-2 text-white font-mono text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-300">Target Return Rate (%)</label>
                        <input
                          type="number" min="5" max="25" value={educationRate}
                          onChange={(e) => setEducationRate(parseFloat(e.target.value) || 0)}
                          className="w-full bg-slate-900 border border-white/10 rounded-xl px-3 py-2 text-white font-mono text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-7 space-y-6">
                  <div className="p-6 rounded-2xl bg-white/5 space-y-4 font-mono">
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest">Compounding Timeframe</span>
                      <span className="text-sm text-white font-bold">{childEducationCalculation.yearsToCollege} Years Left</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest">Inflation-Adjusted Future Cost</span>
                      <span className="text-sm text-white font-bold">{formatCurrency(childEducationCalculation.futureCost)}</span>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-white/5">
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest">Required Monthly SIP</span>
                      <span className="text-2xl font-black text-cyan-400">{formatCurrency(childEducationCalculation.requiredSIP)}</span>
                    </div>
                  </div>

                  <div className="p-4 rounded-xl bg-cyan-500/5 border border-cyan-500/20 text-xs text-slate-300">
                    💡 Preparing early dramatically reduces your required SIP due to decades of geometric compounding. Starting 5 years later doubles the required SIP!
                  </div>
                </div>
              </motion.div>
            )}

            {/* 5. Retirement */}
            {activeTab === 'retirement' && (
              <motion.div
                key="retirement"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center"
              >
                <div className="lg:col-span-5 space-y-6">
                  <h3 className="text-xl font-bold text-white uppercase tracking-wider flex items-center gap-2">
                    <Clock className="text-cyan-400" /> Pension & Retirement Architect
                  </h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-300">Current Age</label>
                        <input
                          type="number" min="18" max="59" value={retirementCurrentAge}
                          onChange={(e) => setRetirementCurrentAge(parseInt(e.target.value) || 18)}
                          className="w-full bg-slate-900 border border-white/10 rounded-xl px-3 py-2 text-white font-mono text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-300">Retirement Age</label>
                        <input
                          type="number" min="60" max="75" value={retirementTargetAge}
                          onChange={(e) => setRetirementTargetAge(parseInt(e.target.value) || 60)}
                          className="w-full bg-slate-900 border border-white/10 rounded-xl px-3 py-2 text-white font-mono text-sm"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-bold text-slate-300">
                        <span>Desired Monthly Expense (Today's value)</span>
                        <span>{formatCurrency(retirementExpenses)}</span>
                      </div>
                      <input
                        type="range" min="10000" max="1000000" step="10000" value={retirementExpenses}
                        onChange={(e) => setRetirementExpenses(parseInt(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-400">Inflation %</label>
                        <input
                          type="number" min="3" max="12" step="0.5" value={retirementInflation}
                          onChange={(e) => setRetirementInflation(parseFloat(e.target.value) || 0)}
                          className="w-full bg-slate-900 border border-white/10 rounded-lg p-2 text-white font-mono text-xs"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-400">Return Pre %</label>
                        <input
                          type="number" min="5" max="20" step="0.5" value={retirementReturnPre}
                          onChange={(e) => setRetirementReturnPre(parseFloat(e.target.value) || 0)}
                          className="w-full bg-slate-900 border border-white/10 rounded-lg p-2 text-white font-mono text-xs"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-400">Return Post %</label>
                        <input
                          type="number" min="4" max="15" step="0.5" value={retirementReturnPost}
                          onChange={(e) => setRetirementReturnPost(parseFloat(e.target.value) || 0)}
                          className="w-full bg-slate-900 border border-white/10 rounded-lg p-2 text-white font-mono text-xs"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-7 space-y-6">
                  <div className="p-6 rounded-2xl bg-white/5 space-y-4 font-mono">
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest">Future Monthly Expense</span>
                      <span className="text-sm text-white font-bold">{formatCurrency(retirementCalculation.futureExpense)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest">Total Corpus Needed (Till Age 85)</span>
                      <span className="text-lg text-emerald-400 font-bold">{formatCurrency(retirementCalculation.corpusNeeded)}</span>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-white/5">
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest">Required Monthly SIP</span>
                      <span className="text-2xl font-black text-cyan-400">{formatCurrency(retirementCalculation.requiredSIP)}</span>
                    </div>
                  </div>

                  <div className="p-4 rounded-xl bg-indigo-500/5 border border-indigo-500/20 text-xs text-slate-300">
                    🛡️ Compounding does the heavy lifting pre-retirement, while capital preservation and post-retirement yield indexation protect your purchasing power from long-term inflation.
                  </div>
                </div>
              </motion.div>
            )}

            {/* 6. Home Purchase */}
            {activeTab === 'home' && (
              <motion.div
                key="home"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center"
              >
                <div className="lg:col-span-5 space-y-6">
                  <h3 className="text-xl font-bold text-white uppercase tracking-wider flex items-center gap-2">
                    <HomeIcon className="text-cyan-400" /> Home Purchase Modeler
                  </h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-bold text-slate-300">
                        <span>Current Target Cost</span>
                        <span>{formatCurrency(homeCurrentCost)}</span>
                      </div>
                      <input
                        type="range" min="1000000" max="50000000" step="500000" value={homeCurrentCost}
                        onChange={(e) => setHomeCurrentCost(parseInt(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-bold text-slate-300">
                        <span>Years to purchase</span>
                        <span>{homeYears} Yrs</span>
                      </div>
                      <input
                        type="range" min="1" max="25" step="1" value={homeYears}
                        onChange={(e) => setHomeYears(parseInt(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-300">Expected Inflation (%)</label>
                        <input
                          type="number" min="4" max="12" step="0.5" value={homeInflation}
                          onChange={(e) => setHomeInflation(parseFloat(e.target.value) || 0)}
                          className="w-full bg-slate-900 border border-white/10 rounded-xl px-3 py-2 text-white font-mono text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-300">Expected SIP returns (%)</label>
                        <input
                          type="number" min="5" max="25" step="0.5" value={homeRate}
                          onChange={(e) => setHomeRate(parseFloat(e.target.value) || 0)}
                          className="w-full bg-slate-900 border border-white/10 rounded-xl px-3 py-2 text-white font-mono text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-7 space-y-6">
                  <div className="p-6 rounded-2xl bg-white/5 space-y-4 font-mono">
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest">Future Cost (Inflation Adjusted)</span>
                      <span className="text-sm text-white font-bold">{formatCurrency(homeCalculation.futureCost)}</span>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-white/5">
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest">Required Monthly SIP</span>
                      <span className="text-2xl font-black text-cyan-400">{formatCurrency(homeCalculation.requiredSIP)}</span>
                    </div>
                  </div>

                  <div className="p-4 rounded-xl bg-cyan-500/5 border border-cyan-500/20 text-xs text-slate-300">
                    🏠 Inflation impacts real estate values severely over time. Planning with adjusted values keeps your financial targets accurate, avoiding sudden credit burdens.
                  </div>
                </div>
              </motion.div>
            )}

            {/* 7. Step up SIP */}
            {activeTab === 'stepup' && (
              <motion.div
                key="stepup"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center"
              >
                <div className="lg:col-span-5 space-y-6">
                  <h3 className="text-xl font-bold text-white uppercase tracking-wider">Step-up SIP Compounding Modeler</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-bold text-slate-300">
                        <span>Initial Monthly SIP</span>
                        <span>{formatCurrency(stepupMonthly)}</span>
                      </div>
                      <input
                        type="range" min="2000" max="100000" step="2000" value={stepupMonthly}
                        onChange={(e) => setStepupMonthly(parseInt(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-bold text-slate-300">
                        <span>Annual Step-up Percentage</span>
                        <span>{stepupAnnualGrowth}%</span>
                      </div>
                      <input
                        type="range" min="5" max="30" step="1" value={stepupAnnualGrowth}
                        onChange={(e) => setStepupAnnualGrowth(parseInt(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-bold text-slate-300">
                        <span>Expected Return (CAGR)</span>
                        <span>{stepupRate}%</span>
                      </div>
                      <input
                        type="range" min="5" max="30" step="0.5" value={stepupRate}
                        onChange={(e) => setStepupRate(parseFloat(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-bold text-slate-300">
                        <span>Time Period (Years)</span>
                        <span>{stepupYears} Yrs</span>
                      </div>
                      <input
                        type="range" min="1" max="35" step="1" value={stepupYears}
                        onChange={(e) => setStepupYears(parseInt(e.target.value))}
                        className="w-full accent-cyan-400"
                      />
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-7 flex flex-col md:flex-row items-center justify-around gap-6 p-6 rounded-2xl bg-white/5">
                  <div className="space-y-4 font-mono w-full md:w-auto">
                    <div>
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest block">Total Invested</span>
                      <span className="text-lg font-bold text-white">{formatCurrency(stepupCalculation.invested)}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest block">Estimated Returns</span>
                      <span className="text-lg font-bold text-emerald-400">+{formatCurrency(stepupCalculation.gains)}</span>
                    </div>
                    <div className="pt-3 border-t border-white/5">
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest block">Total wealth</span>
                      <span className="text-2xl font-black text-cyan-400">{formatCurrency(stepupCalculation.total)}</span>
                    </div>
                  </div>

                  <div className="relative w-40 h-40">
                    <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                      <circle cx="50" cy="50" r="40" stroke="rgba(255,255,255,0.05)" strokeWidth="12" fill="none" />
                      <circle
                        cx="50" cy="50" r="40" stroke="#0ea5e9" strokeWidth="12" fill="none"
                        strokeDasharray="251.2"
                        strokeDashoffset={251.2 - (251.2 * stepupCalculation.invested) / stepupCalculation.total}
                      />
                      <circle
                        cx="50" cy="50" r="40" stroke="#10b981" strokeWidth="12" fill="none"
                        strokeDasharray="251.2"
                        strokeDashoffset={251.2 - (251.2 * stepupCalculation.gains) / stepupCalculation.total}
                        className="origin-center transform rotate-180"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                      <span className="text-[9px] text-slate-500 uppercase font-black">Growth Boost</span>
                      <span className="text-xs font-bold text-slate-200">
                        {((stepupCalculation.total / sipCalculation.total - 1) * 100).toFixed(0)}% More
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* 8. Newton-Raphson XIRR Calculator */}
            {activeTab === 'xirr' && (
              <motion.div
                key="xirr"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-6"
              >
                <div className="flex flex-col lg:flex-row gap-6 justify-between border-b border-white/5 pb-4">
                  <div>
                    <h3 className="text-xl font-bold text-white uppercase tracking-wider">Premium XIRR Analyzer</h3>
                    <p className="text-xs text-slate-400 mt-1">
                      Newton-Raphson numerical engine for computing exact internal rate of return across arbitrary cash flows.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-slate-400">Solved Annualized Rate:</span>
                    <span className="text-2xl font-black text-cyan-400 font-mono">
                      {xirrResult !== null ? `${xirrResult.toFixed(2)}%` : 'Solving...'}
                    </span>
                  </div>
                </div>

                {/* Input Fields */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 rounded-xl bg-white/5 items-end font-mono">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Transaction Date</label>
                    <input
                      type="date" value={xirrDateInput}
                      onChange={(e) => setXirrDateInput(e.target.value)}
                      className="w-full bg-slate-900 border border-white/10 rounded-xl px-3 py-2.5 text-white text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Amount (Inflows = +, Outflows = -)</label>
                    <input
                      type="number" placeholder="e.g. -50000 or 150000" value={xirrAmountInput}
                      onChange={(e) => setXirrAmountInput(e.target.value)}
                      className="w-full bg-slate-900 border border-white/10 rounded-xl px-3 py-2.5 text-white text-xs"
                    />
                  </div>
                  <div>
                    <button
                      onClick={handleAddXirrFlow}
                      className="w-full h-11 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20 rounded-xl text-xs uppercase font-extrabold flex items-center justify-center gap-2 transition-all"
                    >
                      <Plus size={14} /> Add cashflow
                    </button>
                  </div>
                  <div>
                    <button
                      onClick={() => setXirrFlows([
                        { id: 1, date: '2022-01-01', amount: -100000 },
                        { id: 2, date: '2023-01-01', amount: -50000 },
                        { id: 3, date: '2024-01-01', amount: -50000 },
                        { id: 4, date: '2025-05-31', amount: 265000 }
                      ])}
                      className="w-full h-11 bg-white/5 border border-white/5 text-slate-400 hover:bg-white/10 rounded-xl text-xs uppercase font-extrabold flex items-center justify-center gap-2 transition-all"
                    >
                      <RefreshCw size={14} /> Reset Sample
                    </button>
                  </div>
                </div>

                {/* Ledger display table */}
                <div className="overflow-x-auto custom-scrollbar border border-white/5 rounded-xl max-h-60">
                  <table className="w-full text-left border-collapse text-xs font-mono">
                    <thead>
                      <tr className="bg-slate-900 text-slate-400 uppercase tracking-wider text-[10px] border-b border-white/5">
                        <th className="p-3">Order</th>
                        <th className="p-3">Date</th>
                        <th className="p-3">Cashflow (₹)</th>
                        <th className="p-3 text-right">Operation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {xirrFlows.map((flow, index) => (
                        <tr key={flow.id} className="border-b border-white/5 hover:bg-white/5 transition-all text-slate-200">
                          <td className="p-3 text-slate-500">#{index + 1}</td>
                          <td className="p-3">{flow.date}</td>
                          <td className={`p-3 font-bold ${flow.amount < 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                            {flow.amount < 0 ? '-' : '+'}{formatCurrency(Math.abs(flow.amount))}
                          </td>
                          <td className="p-3 text-right">
                            <button
                              onClick={() => handleRemoveXirrFlow(flow.id)}
                              className="p-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-all"
                            >
                              <Trash2 size={13} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="p-4 rounded-xl bg-cyan-500/5 border border-cyan-500/20 text-xs text-slate-300">
                  ⚠️ <strong>XIRR Guideline:</strong> Ensure your investment deposits are formatted as negative values (cash outflows) and final valuation or redemption amount is formatted as a positive value (cash inflow) on the current date to solve correctly.
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </section>

      {/* Value Proposition Grid (How We Help) */}
      <section id="help" className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative z-10">
        <div className="text-center max-w-2xl mx-auto space-y-4 mb-16">
          <span className="text-xs font-black uppercase tracking-[0.25em] text-cyan-400">PARTNER IN WEALTH</span>
          <h2 className="text-3xl sm:text-5xl font-black text-white">How We Help?</h2>
          <p className="text-slate-400 text-sm sm:text-base">
            We align with your long-term roadmap, integrating discipline, monitoring, and cycle guidance to guard and build your sovereign wealth.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            {
              title: 'Clarity in Financial Decisions',
              desc: 'Removing ambiguity from asset classes with absolute transparent mapping to actual timelines.',
              icon: <BookOpen className="text-cyan-400" />
            },
            {
              title: 'Goal Based Investment Planning',
              desc: 'Aligning each SIP, SWP, and fund selection strictly with your milestones like education or retirement.',
              icon: <CheckCircle className="text-cyan-400" />
            },
            {
              title: 'Disciplined Investment Strategies',
              desc: 'Safeguarding capital from emotional decisions during volatile cycles with systematic investing.',
              icon: <Activity className="text-cyan-400" />
            },
            {
              title: 'Portfolio Monitoring & Review',
              desc: 'Regular institutional tracking and evaluation of funds to maintain target yield allocation metrics.',
              icon: <TrendingUp className="text-cyan-400" />
            },
            {
              title: 'Guidance Every Market Cycle',
              desc: 'Helping you navigate transitions between bull runs and corrections with professional security.',
              icon: <ShieldCheck className="text-cyan-400" />
            },
            {
              title: 'Long Term Partner in Wealth',
              desc: 'Standing by your family across generations to maintain consistency and compound asset growth.',
              icon: <Award className="text-cyan-400" />
            }
          ].map((item, idx) => (
            <motion.div
              key={idx}
              whileHover={{ y: -4, borderColor: 'rgba(6, 182, 212, 0.3)' }}
              className="bg-[#050a24]/50 border border-white/5 rounded-2xl p-6 transition-all space-y-4 flex flex-col justify-between"
            >
              <div className="space-y-4">
                <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/5 flex items-center justify-center">
                  {item.icon}
                </div>
                <h3 className="font-extrabold text-white text-base tracking-wide">{item.title}</h3>
                <p className="text-slate-400 text-xs leading-relaxed">{item.desc}</p>
              </div>
              <div className="pt-2 flex items-center text-[10px] font-black uppercase text-cyan-400 tracking-wider gap-1.5 cursor-pointer hover:underline">
                <span>Explore dynamic strategy</span> <ChevronRight size={12} />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Trust, AMFI Compliance & Partners Section */}
      <section className="py-16 border-t border-white/5 bg-[#01030e] relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">

          <div className="flex flex-col md:flex-row items-center justify-between gap-6 border-b border-white/5 pb-8">
            <div className="flex items-center space-x-3.5">
              <ShieldCheck className="w-8 h-8 text-cyan-400 animate-pulse" />
              <div>
                <span className="text-[13px] text-slate-500 font-bold uppercase tracking-wider block">AMFI Regulatory Registration</span>
                <span className="text-base font-extrabold text-white">Registered Mutual Fund Advisor: 340820</span>
              </div>
            </div>
            <div className="text-xs text-slate-400 font-mono">
              Regulated framework mapping to national compliance guidelines.
            </div>
          </div>

          <div className="space-y-6">
            <h4 className="text-[10px] font-black uppercase tracking-[0.25em] text-cyan-500 text-center">
              Secured Sovereign Transaction Partners
            </h4>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              {[
                { name: 'NSE NMF-II', logo: nsemf2, type: 'Exchange Transaction Engine' },
                { name: 'MF Utility', logo: mfutility, type: 'Universal MF Portal' },
                { name: 'CAMS', logo: camslogo, type: 'Registrar & Transfer Agent' },
                { name: 'Kfintech', logo: kfintechlogo, type: 'Registrar & Transfer Agent' }
              ].map((partner, idx) => (
                <div key={idx} className="p-4 rounded-xl bg-white/5 border border-white/5 font-mono flex flex-col items-center justify-between gap-4 hover:bg-white/10 transition-colors">
                  <div className="w-full bg-white rounded-lg p-2 flex items-center justify-center h-16 overflow-hidden">
                    <img src={partner.logo} alt={partner.name} className="max-h-full max-w-full object-contain mix-blend-multiply" />
                  </div>
                  <span className="text-[9px] text-slate-400 block uppercase text-center leading-tight">{partner.type}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* Disclaimer Section */}
      <div className="py-8 bg-[#01020a]/50 relative z-10 px-4 sm:px-6 lg:px-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center gap-4 bg-blue-950/20 border border-blue-500/20 p-4 rounded-xl">
            <ArrowRight className="text-blue-500 shrink-0 animate-pulse" size={28} />
            <p className="leading-relaxed text-left font-sans text-slate-300" style={{ fontSize: "14px" }}>
              <strong className="text-blue-400 animate-pulse">Mutual Fund Disclaimer:</strong> “Mutual Fund investments are subject to market risks, including the possible loss of principal. Past performance is not indicative of future results. Please read all scheme-related documents carefully before investing.”
            </p>
          </div>
        </div>
      </div>

      {/* Global Reusable Footer */}
      <Footer />

    </div>
  );
};

export default MutualFunds;
